<template>
  <Popover :key="popoverKey" class="group">
    <PopoverButton
      role="button"
      :as="to ? 'a' : 'div'"
      :href="to"
      class="flex gap-1 cursor-pointer text-menu-link font-medium items-center relative"
      @mouseenter="openPopover"
      @mousedown="openPopover"
      @mouseleave="closePopover"
      @click="handleClick">
      <span class="flex gap-1 items-center">
        <CountryFlag v-if="region" :code="region" />
        <span class="whitespace-nowrap text-base">{{ $t(label) }} </span>
      </span>
      <ChevronDownIcon class="size-3 transition duration-300 group-hover:rotate-180" />
      <span
        class="absolute -bottom-1 left-0 h-[4px] bg-primary max-w-[50%] transition-[width] duration-150 ease-out"
        :class="highlight() ? 'w-1/2' : 'w-0 group-hover:w-1/2'" />
    </PopoverButton>
    <transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="translate-y-1 opacity-0"
      enter-to-class="translate-y-0 opacity-100"
      leave-active-class="transition duration-150 ease-in"
      leave-from-class="translate-y-0 opacity-100"
      leave-to-class="translate-y-1 opacity-0">
      <PopoverPanel
        v-if="isOpen"
        as="div"
        static
        class="absolute z-20 overflow-hidden rounded-b-lg focus:outline-none w-fit"
        style="
          top: var(--header-height);
          box-shadow:
            0px 20px 25px -5px rgba(0, 0, 0, 0.1),
            0px 10px 10px 0px rgba(0, 0, 0, 0.04);
        "
        @mouseenter="openPopover"
        @mouseleave="closePopover">
        <slot></slot>
      </PopoverPanel>
    </transition>
  </Popover>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/vue";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";
import CountryFlag from "@/components/CountryFlag.vue";
import { useTimeoutFn } from "@vueuse/core";

import { useI18n } from "vue-i18n";
const { t } = useI18n();
const router = useRouter();

const { start: startCloseTimer, stop: cancelCloseTimer } = useTimeoutFn(() => {
  isOpen.value = false;
}, 200);

const popoverKey = ref(0);
const route = useRoute();

const isOpen = ref(false);

const openPopover = () => {
  cancelCloseTimer();
  isOpen.value = true;
};

const closePopover = () => {
  startCloseTimer();
};

watch(route, () => {
  popoverKey.value++;
  isOpen.value = false;
});

const props = defineProps(["label", "region", "to"]);

const highlight = () => {
  if (
    (route.query?.sport_type && t(`${route.query.sport_type}`) === `${t(props.label).toLowerCase()}`) ||
    (route.fullPath === "/events?sport_type=motorsport,motogp" && props.label === `${t("Other Motorsports")}`) ||
    (route.fullPath === "/events?sport_type=horseracing" && props.label === `${t("Other events")}`) ||
    (route.fullPath === "/football/tournaments" && props.label === `${t("Football")}`) ||
    (route.fullPath === "/tennis/tournaments" && props.label === `${t("Tennis")}`) ||
    isOpen.value
  ) {
    return "underline";
  }
};

const handleClick = (e) => {
  if (props.to) {
    // Navigate to the page on click
    e.preventDefault();
    router.push(props.to);
    isOpen.value = false;
  }
};
</script>
